import { useQuery, UseQueryResult } from '@tanstack/react-query'
import { useFetchAllRows, UseFetchAllRowsResult } from '@/hooks/useFetchAllRows'
import { API_COMMON_CACHE_TIME } from '@/utils/consts'
import { SelectOptionType } from '@/components/content/SelectBoxTypes'
import { getGoodsCodeOptions, getInventoryStatuses, getWarehouseNameCombo } from './apis'
import { GetGoodsCodeOptionsReq, GetInventoryStatusesReq, GoodsCodeOption, InventoryStatusItem } from './types'

const PATH = ['inventory', 'inventoryStatus'] as const

/**
 * `data` 를 도메인 이름(`items`·`options`)으로 바꿔 내보내는 훅의 반환 타입.
 * 세 훅이 같은 모양이라 한 곳에 둔다 — CLAUDE.md 의 "React Query 훅 반환 타입 명시 필수" 규칙.
 */
type RenamedQueryResult<TData, TKey extends string> = Omit<UseQueryResult<TData, Error>, 'data'> & Record<TKey, TData>

export const inventoryStatusQueryKeys = {
  all: PATH,
  // 맨 앞 메뉴코드 — 탭을 닫으면 MenuOnSessionWrap.remove 가 전량 캐시를 지운다(LOGISPRD-15272).
  list: (params: GetInventoryStatusesReq) => ['st09', ...PATH, 'LIST', params] as const,
  warehouseNames: [...PATH, 'WAREHOUSE_NAMES'] as const,
  goodsCodes: (params: GetGoodsCodeOptionsReq) => [...PATH, 'GOODS_CODES', params] as const,
  /** 잠금 계정의 자기 화주(유형을 알기 위해) — LOGISPRD-14949 */
  ownShipper: (shipperName: string) => [...PATH, 'OWN_SHIPPER', shipperName] as const,
}

/** 집계 행이라 기본키가 없다 — 1번 호출이라 구간 경계 중복도 없으므로 응답 순번을 키로 쓴다. */
export type InventoryStatusRow = InventoryStatusItem & { rowIndex: number }
const getRowKey = (row: InventoryStatusRow): number => row.rowIndex

/**
 * ST02 재고현황 목록 — 1.0 방식 전량 조회(LOGISPRD-15272). BE 가 size 를 쓰지 않고 전건을 주므로 1번 호출로 받는다.
 *
 * `enabled` 는 화면이 정한다 — 필수 필터(화주·센터)가 채워진 조건이 있을 때만 켠다.
 * 무필터 조회는 BE 가 400 으로 막는 경로라 조건 없이 켜면 진입 때마다 실패한다.
 */
export const useGetInventoryStatuses = (params: GetInventoryStatusesReq, enabled: boolean): UseFetchAllRowsResult<InventoryStatusRow> =>
  useFetchAllRows({
    queryKey: inventoryStatusQueryKeys.list(params),
    fetchPage: async () => {
      const res = await getInventoryStatuses(params)
      const items = (res.data?.items ?? []).map((item, rowIndex) => ({ ...item, rowIndex }))
      return { items, totalCount: items.length }
    },
    getKey: getRowKey,
    enabled,
  })

/**
 * 센터명 콤보 옵션. 변경 빈도가 낮아 공용 캐시 시간을 그대로 쓴다.
 *
 * 콤보 API 는 등록 순서대로 내려주므로 **여기서 이름순으로 정렬**한다.
 * `numeric` 을 켜야 '이천2' 가 '이천10' 앞에 온다(문자열 비교로는 뒤로 밀린다).
 */
export const useGetWarehouseNameOptions = (enabled = true): RenamedQueryResult<SelectOptionType<string>[], 'options'> => {
  const { data, ...rest } = useQuery({
    enabled,
    queryKey: inventoryStatusQueryKeys.warehouseNames,
    queryFn: async (): Promise<SelectOptionType<string>[]> => {
      const res = await getWarehouseNameCombo()
      // 콤보는 코드/명 배열이 짝을 이룬다. 명(COMBO_OPT_NM)이 비면 코드로 대체한다.
      const options = (res?.WH_NM?.COMBO_OPT_CD ?? []).map((code, idx) => ({
        value: code,
        label: res?.WH_NM?.COMBO_OPT_NM?.[idx] || code,
      }))

      return options.sort((a, b) => a.label.localeCompare(b.label, 'ko', { numeric: true }))
    },
    staleTime: API_COMMON_CACHE_TIME,
  })

  return { options: data ?? [], ...rest }
}

/**
 * 상품코드 서치헬퍼 목록. 조회 조건(화주·브랜드명·상품명)별로 캐시한다.
 *
 * `enabled` 를 꺼두면 조회를 실행하지 않는다 — 헬퍼 모달은 열자마자 전건을 긁지 않고
 * 사용자가 조회를 누른 뒤에만 부른다(1.0 헬퍼 동치, 화주 상품 전건 distinct 라 응답이 클 수 있다).
 */
export const useGetGoodsCodeOptions = (params: GetGoodsCodeOptionsReq, enabled = true): RenamedQueryResult<GoodsCodeOption[], 'items'> => {
  const { data, ...rest } = useQuery({
    enabled,
    queryKey: inventoryStatusQueryKeys.goodsCodes(params),
    queryFn: async () => {
      const res = await getGoodsCodeOptions(params)
      return res.data?.items ?? []
    },
    staleTime: API_COMMON_CACHE_TIME,
  })

  return { items: data ?? [], ...rest }
}
